import { Link, useRouterState } from '@tanstack/react-router';
import { LogOut } from 'lucide-react';
import { cn } from '@/lib/utils';
import { signOut, useSession } from '../../lib/auth-client';
import { BrandMark } from './BrandMark';
import { InboxBadge, useInboxCount } from './InboxBadge';
import { isActivePath, type NavItem, PRIMARY_NAV, SECONDARY_NAV } from './nav';
import { ThemeToggle } from './ThemeToggle';

export function Sidebar() {
  const { data: session } = useSession();
  const currentPath = useRouterState().location.pathname;
  const inboxCount = useInboxCount();

  const handleLogout = async () => {
    await signOut();
    window.location.href = '/login';
  };

  const renderItem = (item: NavItem, primary: boolean) => {
    const isActive = isActivePath(currentPath, item.to);
    const Icon = item.icon;
    return (
      <Link
        key={item.to}
        to={item.to}
        aria-current={isActive ? 'page' : undefined}
        className={cn(
          'flex items-center gap-3 rounded-lg px-3 font-medium transition-colors',
          primary ? 'h-10 text-[15px]' : 'h-9 text-sm',
          isActive
            ? 'bg-card text-foreground shadow-xs ring-1 ring-border'
            : 'text-muted-foreground hover:bg-muted hover:text-foreground',
        )}
      >
        <Icon className={cn('shrink-0', primary ? 'size-[18px]' : 'size-4')} />
        {item.label}
        {item.badge === 'inbox' && <InboxBadge count={inboxCount} className="ml-auto" />}
      </Link>
    );
  };

  return (
    <aside className="hidden border-r border-border bg-sidebar lg:fixed lg:inset-y-0 lg:flex lg:w-60 lg:flex-col">
      <div className="flex h-16 items-center px-5">
        <BrandMark />
      </div>

      <nav aria-label="Huvudmeny" className="flex-1 space-y-6 overflow-y-auto px-3 pb-4">
        <div className="space-y-1">{PRIMARY_NAV.map((item) => renderItem(item, true))}</div>
        {SECONDARY_NAV.map((section) => (
          <div key={section.label}>
            <p className="mb-1 px-3 text-xs font-medium text-muted-foreground">{section.label}</p>
            <div className="space-y-0.5">
              {section.items.map((item) => renderItem(item, false))}
            </div>
          </div>
        ))}
      </nav>

      <div className="space-y-3 border-t border-border px-4 py-3">
        <ThemeToggle />
        <div className="flex items-center gap-3">
          <div className="flex size-8 items-center justify-center rounded-full bg-muted text-xs font-semibold">
            {session?.user?.name?.[0]?.toUpperCase() ?? '?'}
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium">{session?.user?.name}</p>
            <p className="truncate text-xs text-muted-foreground">{session?.user?.email}</p>
          </div>
          <button
            type="button"
            onClick={handleLogout}
            className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            aria-label="Logga ut"
          >
            <LogOut className="size-4" />
          </button>
        </div>
      </div>
    </aside>
  );
}
